import axios from "./axios";

export interface AdminNotificationPayload {
  userId: string;
  title: string;
  message: string;
  type?: string;
  contentId?: string;
  contentPartId?: string;
  actionUrl?: string;
}

export interface AdminNotificationBroadcastPayload {
  title: string;
  message: string;
  type?: string;
  contentId?: string;
  contentPartId?: string;
  actionUrl?: string;
  userIds?: string[];
  roleNames?: string[];
  sendToAll?: boolean;
}

export interface AdminNotificationItem {
  id: string;
  title: string;
  message: string;
  type?: string;
  isRead?: boolean;
  createdAtUtc?: string;
  actionUrl?: string;
}

export interface AdminNotificationHistoryItem {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAtUtc: string;
}

export interface AdminNotificationStats {
  totalSent: number;
  unreadCount: number;
  daily: { dateUtc: string; count: number }[];
  typeBreakdown: Record<string, number>;
  rangeStartUtc: string;
  rangeEndUtc: string;
}

export async function sendAdminNotification(payload: AdminNotificationPayload): Promise<boolean> {
  try {
    const res = await axios.post("/admin/notifications", payload);
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    console.error("sendAdminNotification error", error);
    return false;
  }
}

export async function broadcastAdminNotification(payload: AdminNotificationBroadcastPayload): Promise<boolean> {
  try {
    const body = {
      title: payload.title,
      message: payload.message,
      type: payload.type,
      contentId: payload.contentId,
      contentPartId: payload.contentPartId,
      actionUrl: payload.actionUrl,
      userIds: payload.userIds?.filter((id) => Boolean(id)) ?? undefined,
      roleNames: payload.roleNames?.filter((name) => Boolean(name)) ?? undefined,
      sendToAll: payload.sendToAll ?? false,
    };
    const res = await axios.post("/admin/notifications/broadcast", body);
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    console.error("broadcastAdminNotification error", error);
    return false;
  }
}

export async function getNotificationsForUser(userId: string, take = 10): Promise<AdminNotificationItem[]> {
  if (!userId) {
    return [];
  }
  try {
    const res = await axios.get(`/admin/notifications/user/${userId}`, { params: { take } });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("getNotificationsForUser error", error);
    return [];
  }
}

export async function getNotificationHistory(take = 20): Promise<AdminNotificationHistoryItem[]> {
  try {
    const res = await axios.get("/admin/notifications/history", { params: { take } });
    return Array.isArray(res.data) ? (res.data as AdminNotificationHistoryItem[]) : [];
  } catch (error) {
    console.error("getNotificationHistory error", error);
    return [];
  }
}

export async function getNotificationStats(days = 7): Promise<AdminNotificationStats | null> {
  try {
    const res = await axios.get("/admin/notifications/stats", { params: { days } });
    return res.data as AdminNotificationStats;
  } catch (error) {
    console.error("getNotificationStats error", error);
    return null;
  }
}
